import * as React from 'react';
import TimeTaxSupportSystemClient from '../../system';
import { DateTime } from 'luxon';
import { ComboBox, DatePicker, IComboBox, IComboBoxOption, PrimaryButton } from '@fluentui/react';
import { DateCustom } from '../../lib/DateCustom';
import MessageCourier from '../../lib/MessageCourier';
import { useTranslation } from 'react-i18next';
import { getLocalizedStrings } from '../../lib/LocalizedStringsForDatePicker';
import { User } from '../../system/User';
import useIsMobile from '../../common/custom-hooks/useIsMobile';
import useSystemCall from '../../common/custom-hooks/useSystemCall';
import { ALL_SECTORS, OrganizationChartDropdown } from '../Common/OrganizationChartDropdown';
import { ICollaboratorBySector } from '../../system/collaborators/CollaboratorSystem';

interface IReportingFiltersPeriodo {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  onChangePeriodDate: (date: DateTime) => void;
  onChangeSectorId: (id: string) => void;
  onChangeCollaboratorId: (id: string) => void;
  onClickSearch: () => void;
  user?: User;
}

export const ReportingFiltersPeriodo = (props: IReportingFiltersPeriodo) => {
  const { t } = useTranslation();
  const [fromDate, setPeriodDate] = React.useState<DateTime | undefined>(undefined);
  const [shouldRefresh, setShouldRefresh] = React.useState<boolean>(false);
  const [selectedSectorId, setSelectedSectorId] = React.useState<string | undefined>(undefined);
  const [selectedCollaboratorId, setSelectedCollaboratorId] = React.useState<string | undefined>(undefined);
  const [collaboratorsBySector, setCollaboratorsBySector] = React.useState<ICollaboratorBySector[]>([]);
  const [loadingCollaborators, setLoadingCollaborators] = React.useState<boolean>(false);
  const { isMobile } = useIsMobile();

  const nodes = useSystemCall(props.system, (system) => system.getOrganizationChartSystem().getAllNodes());

  React.useEffect(() => {
    setPeriodDate(DateTime.fromJSDate(new Date()));
    props.onChangePeriodDate(DateTime.fromJSDate(new Date()));
  }, []);

  React.useEffect(() => {
    const fetchCollaborators = async () => {
      if (selectedSectorId) {
        setLoadingCollaborators(true);
        setCollaboratorsBySector([]);
        setSelectedCollaboratorId('ALL');
        props.onChangeCollaboratorId('');
        try {
          const data = await props.system.getCollaboratorSystem().getCollaboratorsBySector(selectedSectorId);
          setCollaboratorsBySector(data);
        } catch (error) {
          console.error('Error fetching collaborators by sector:', error);
        } finally {
          setLoadingCollaborators(false);
        }
      } else {
        setCollaboratorsBySector([]);
        setSelectedCollaboratorId('ALL');
        props.onChangeCollaboratorId('');
      }
    };

    fetchCollaborators();
  }, [selectedSectorId]);

  const collaboratorOptions: IComboBoxOption[] = React.useMemo(() => {
    const options = collaboratorsBySector.map((col) => ({
      key: col.col_id,
      text: `${col.col_nombre} ${col.col_apellido}`,
    }));
    if (options.length > 0) {
      options.unshift({ key: 'ALL', text: t('Todos') });
    }
    return options;
  }, [collaboratorsBySector, t]);

  const onChangeDate = (date: Date | null | undefined, name: string) => {
    if (date) {
      if (name === 'from') {
        setPeriodDate(DateTime.fromJSDate(date));
        props.onChangePeriodDate(DateTime.fromJSDate(date));
        setShouldRefresh(!shouldRefresh);
        if (props.onChangePeriodDate) props.onChangePeriodDate(DateTime.fromJSDate(date));
      }
    }
  };

  const onChangeSector = (node: any) => {
    const nodeId = node.getId ? node.getId() : String(node.key);
    setSelectedSectorId(nodeId);
    props.onChangeSectorId(nodeId);
  };

  const onChangeCollaborator = (event: React.FormEvent<IComboBox>, option?: IComboBoxOption) => {
    if (option) {
      const collId = String(option.key);
      if (collId === 'ALL') {
        setSelectedCollaboratorId('ALL');
        props.onChangeCollaboratorId('');
      } else {
        setSelectedCollaboratorId(collId);
        props.onChangeCollaboratorId(collId);
      }
    } else {
      setSelectedCollaboratorId('ALL');
      props.onChangeCollaboratorId('');
    }
  };

  return (
    <div className="ms-Grid" dir="ltr">
      <div className="ms-Grid-row mt-12" style={{maxWidth: 1600}}>
        {isMobile ? (
          <div className="ms-Grid-col ms-sm12 mt-1" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: '2px' }}>
            <span className="ms-sm6">{t('Filtro')}</span>
            <PrimaryButton iconProps={{ iconName: 'Search' }} className="ms-sm6 ml-2" onClick={props.onClickSearch} text={t('Buscar')} />
          </div>
        ) : null}

        <div className="ms-Grid-col ms-md3 ms-sm6 mt-1">
          <DatePicker
            strings={getLocalizedStrings()}
            label={t('Periodo')}
            formatDate={DateCustom.formatDateMMYYYY}
            value={fromDate ? fromDate.toJSDate() : undefined}
            onSelectDate={(e) => onChangeDate(e, 'from')}
            calendarProps={{
              isDayPickerVisible: false,
              showGoToToday: false,
            }}
          />
        </div>

        {nodes && (
          <div className="ms-Grid-col ms-md3 ms-sm6 mt-1">
            <OrganizationChartDropdown
              options={nodes}
              label={t('Sector')}
              onChange={onChangeSector}
              selectedKey={selectedSectorId}
              useAllSector={props.user?.hasPowerUserPrivileges()}
              onSelectAll={() => {
                setSelectedSectorId(ALL_SECTORS);
                props.onChangeSectorId(ALL_SECTORS);
              }}
              placeholder={t('Seleccionar un sector')}
            />
          </div>
        )}

        <div className="ms-Grid-col ms-md3 ms-sm6 mt-1">
          <ComboBox
            label={t('Colaborador')}
            options={collaboratorOptions}
            selectedKey={selectedCollaboratorId}
            onChange={onChangeCollaborator}
            disabled={!selectedSectorId || loadingCollaborators}
            allowFreeform
            autoComplete="on"
            placeholder={loadingCollaborators ? t('Cargando...') : t('Seleccionar colaborador')}
          />
        </div>

        <div className="ms-Grid-col ms-sm2 mt-1" style={{ display: isMobile ? 'none' : '' }}>
          <PrimaryButton iconProps={{ iconName: 'Search' }} className="mt-for-btn-search ml-2" onClick={props.onClickSearch} text={t('Buscar')} />
        </div>
      </div>
    </div>
  );
};
